/**
 * Retrieves a feature flag value from the local storage cache.
 *
 * This function searches through the local storage for keys that match the
 * pattern `HARNESS_FF_CACHE_` and do not end with `.ts`. It then parses the
 * cached flags and returns the value of the feature flag specified by the
 * provided key.
 *
 * @param key - The key of the feature flag to retrieve.
 * @returns The boolean value of the feature flag if found it can be `true` or `false` or `undefined` if not found.
 */
export const getFeatureFlagValueFromCache = (key: string): boolean | undefined => {
  const storageKeyPrefix = /^HARNESS_FF_CACHE_(?!.*\.ts$)/;
  // Loop through all localStorage items
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    // Only consider keys that match the prefix
    if (storageKey && storageKeyPrefix.test(storageKey)) {
      const cachedFlags = localStorage.getItem(storageKey);
      if (cachedFlags) {
        const parsedFlags = tryParseJSON(cachedFlags);
        const value =
          Array.isArray(parsedFlags) &&
          parsedFlags?.find((f: Record<string, unknown>) => f.flag === key)?.value;
        // Return true/false if the flag value is a string 'true'/'false'
        // Return undefined if the flag value is not found
        if (value && value === 'true') return true;
        if (value && value === 'false') return false;
      }
    }
  }
};

// Helper function to safely parse JSON
function tryParseJSON(
  jsonString: string
): Array<Record<string, unknown>> | Record<string, unknown> | null {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null; // Return null if parsing fails
  }
}
